import React, { useRef, useEffect, useState } from "react";
import "./CurrencyInput.scss";

import NumberFormat, { NumberFormatValues } from "react-number-format";
import { splitNumbers } from "../../../utils/methods";
import { CurrencyPickerDropdown } from "../../project/TableField/components/CurrencyPickerDropdown/CurrencyPickerDropdown";

interface Props {
  inputRef?: React.RefObject<HTMLInputElement>;
  rawValue?: string;
  setRawValue: React.Dispatch<React.SetStateAction<string>>;
  onFocus?: () => void;
  onBlur?: () => void;
  readOnly?: boolean;
  className?: string;
  placeholder?: string;
  prefix?: string;
  inlinePrefix?: string;
  columnCurrencyCode?: string;
  onCurrencyChange?: (newCurrencyCode: string) => void;
  onCurrencyPickerOpenChange?: (isOpen: boolean) => void;
}

export function CurrencyInput(props: Props) {
  const {
    rawValue,
    setRawValue,
    inputRef,
    prefix,
    inlinePrefix,
    columnCurrencyCode,
    onCurrencyChange,
    onCurrencyPickerOpenChange,
    onFocus,
    onBlur,
    ...rest
  } = props;
  const [formattedValue, setFormattedValue] = useState<string | number>(
    splitNumbers(rawValue!),
  );
  const [width, setWidth] = useState(0);
  const [isValueInputFocused, setIsValueInputFocused] = useState(false);
  const span = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setupWidth();
  }, [formattedValue]);

  const validate = (values: NumberFormatValues) => {
    const { floatValue } = values;
    if (!floatValue) return true;
    return floatValue > -10e13 && floatValue < 10e13;
  };

  const setupWidth = () => {
    if (!inlinePrefix) {
      if (span.current?.offsetWidth! < 7) setWidth(10);
      else setWidth(span.current?.offsetWidth!);
    }
  };

  return (
    <>
      {onCurrencyChange
        ? prefix && (
            <CurrencyPickerDropdown
              currentCurrencyCode={prefix}
              columnCurrencyCode={columnCurrencyCode || prefix}
              onSelect={onCurrencyChange}
              onOpenChange={onCurrencyPickerOpenChange}
              returnFocusRef={inputRef}
              forceVisible={isValueInputFocused}
            />
          )
        : prefix && (
            <span
              className="currency-code"
              onClick={() => inputRef?.current?.focus()}
            >
              {prefix}
            </span>
          )}
      <NumberFormat
        className="currency-input"
        value={rawValue}
        decimalScale={2}
        onValueChange={(values) => {
          const { formattedValue, value, floatValue } = values;
          setFormattedValue(formattedValue);
          setRawValue(floatValue?.toString() || "");
        }}
        thousandSeparator=" "
        isAllowed={validate}
        getInputRef={inputRef}
        prefix={inlinePrefix}
        style={!inlinePrefix ? { width } : {}}
        onFocus={() => {
          setIsValueInputFocused(true);
          onFocus?.();
        }}
        onBlur={() => {
          setIsValueInputFocused(false);
          onBlur?.();
        }}
        {...rest}
      />
      <span className="currency-value currency-value--hidden" ref={span}>
        {formattedValue}
      </span>
    </>
  );
}
